/*
 * Copyright (c) 2021 SARL SIGMA POLYNESIA
 */

import { IParseOptions, IStringifyOptions, parse, stringify } from 'qs'
import { SetStateAction, useCallback, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router'

const parseOptions: IParseOptions = {
  ignoreQueryPrefix: true
}

const stringifyOptions: IStringifyOptions = {
  skipNulls: true
}

export type QueryParams = Record<string, string | string[]>

/**
 * Returns search params from current location.
 */
function useQueryParams (): [QueryParams, (params: SetStateAction<QueryParams>) => QueryParams] {
  const location = useLocation()
  const navigate = useNavigate()

  const paramsRef = useRef<QueryParams>(null)

  const params: QueryParams = useMemo(() => {
    const result: QueryParams = {}

    if (location.search) {
      const parsed = parse(location.search, parseOptions)
      Object.entries(parsed).forEach(([k, v]) => {
        if (typeof v === 'string' || v instanceof Array) {
          result[k] = v
        }
      })
    }
    paramsRef.current = result
    return result
  }, [location.search])

  const setQueryParams = useCallback((next: SetStateAction<QueryParams>) => {
    let result

    if (typeof next === 'function') {
      result = next(paramsRef.current)
    } else {
      result = next
    }
    navigate(`?${stringify(result, stringifyOptions)}`)
    return result
  }, [navigate])

  return [params, setQueryParams]
}

export default useQueryParams
